import React, { useState, useEffect } from 'react';
import { Users, Heart, Sparkles, TrendingUp } from 'lucide-react';

export const LiveVisitorCounter: React.FC = () => {
  // Base visitor count initialized from localStorage or default realistic baseline
  const [totalVisitors, setTotalVisitors] = useState<number>(() => {
    if (typeof window === 'undefined') return 184920;
    try {
      const stored = localStorage.getItem('lovescoretest_total_visitors');
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed > 0) return parsed;
      }
      const base = 184920;
      localStorage.setItem('lovescoretest_total_visitors', base.toString());
      return base;
    } catch {
      return 184920;
    }
  });

  const [activeNow, setActiveNow] = useState<number>(84);
  const [testsCompleted, setTestsCompleted] = useState<number>(342150);

  useEffect(() => {
    // Record this session visit if not already counted in this tab session
    try {
      const sessionCounted = sessionStorage.getItem('lovescoretest_session_counted');
      if (!sessionCounted) {
        setTotalVisitors((prev) => {
          const updated = prev + 1;
          localStorage.setItem('lovescoretest_total_visitors', updated.toString());
          return updated;
        });
        sessionStorage.setItem('lovescoretest_session_counted', 'true');
      }
    } catch {
      // ignore
    }

    // Micro-simulation for live dynamic visitors
    const interval = setInterval(() => {
      // Occasional random visitor increment
      if (Math.random() > 0.4) {
        setTotalVisitors((prev) => {
          const next = prev + 1;
          try {
            localStorage.setItem('lovescoretest_total_visitors', next.toString());
          } catch {
            // ignore
          }
          return next;
        });

        setTestsCompleted((prev) => prev + Math.floor(Math.random() * 2) + 1);
      }

      // Small natural fluctuation in active users online (between 72 and 116)
      setActiveNow((prev) => {
        const delta = Math.floor(Math.random() * 5) - 2;
        const next = prev + delta;
        return Math.min(Math.max(next, 72), 116);
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white via-pink-50/40 to-purple-50/30 dark:from-slate-900 dark:via-slate-900/90 dark:to-slate-950 border border-pink-200/80 dark:border-slate-800 p-6 md:p-8 shadow-sm">
        {/* Subtle decorative glow */}
        <div className="absolute top-0 right-0 -mt-8 -mr-8 w-48 h-48 bg-pink-500/10 dark:bg-pink-500/5 rounded-full blur-2xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -mb-8 -ml-8 w-48 h-48 bg-purple-500/10 dark:bg-purple-500/5 rounded-full blur-2xl pointer-events-none" />

        <div className="relative z-10">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-pink-100 dark:border-slate-800">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-100/80 dark:bg-pink-950/60 border border-pink-200 dark:border-pink-900/50 text-pink-700 dark:text-pink-300 text-xs font-bold mb-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <span>Live Visitor Counter</span>
              </div>
              <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                Live Community & Visitor Tracker
              </h2>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">
                Real-time tracking of users and couples discovering compatibility on LoveScoreTest.com
              </p>
            </div>

            <div className="flex items-center gap-2 self-start sm:self-auto px-3.5 py-1.5 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-xs">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                <span className="text-emerald-700 dark:text-emerald-400 font-extrabold">{activeNow}</span> People Online Now
              </span>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 pt-6">
            {/* Total Visitors Card */}
            <div className="p-5 rounded-2xl bg-white dark:bg-slate-800/80 border border-pink-100 dark:border-slate-700/80 shadow-xs flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-pink-100 dark:bg-pink-950/80 text-pink-700 dark:text-pink-400 flex items-center justify-center shrink-0">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                  Total Website Visitors
                </span>
                <div className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                  {totalVisitors.toLocaleString()}
                </div>
                <span className="text-[10px] font-medium text-emerald-700 dark:text-emerald-400 flex items-center gap-1 mt-0.5">
                  <TrendingUp className="w-3 h-3" /> Real-time active counter
                </span>
              </div>
            </div>

            {/* Compatibility Tests Taken */}
            <div className="p-5 rounded-2xl bg-white dark:bg-slate-800/80 border border-pink-100 dark:border-slate-700/80 shadow-xs flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-purple-100 dark:bg-purple-950/80 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0">
                <Heart className="w-6 h-6 fill-purple-500/20" />
              </div>
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                  Compatibility Tests Run
                </span>
                <div className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                  {testsCompleted.toLocaleString()}
                </div>
                <span className="text-[10px] font-medium text-slate-600 dark:text-slate-400 mt-0.5 block">
                  Across all 100 relationship tools
                </span>
              </div>
            </div>

            {/* Global User Satisfaction */}
            <div className="p-5 rounded-2xl bg-white dark:bg-slate-800/80 border border-pink-100 dark:border-slate-700/80 shadow-xs flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-rose-100 dark:bg-rose-950/80 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                  Avg Couple Compatibility
                </span>
                <div className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                  81.6%
                </div>
                <span className="text-[10px] font-medium text-pink-700 dark:text-pink-400 mt-0.5 block">
                  Based on recent calculations
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
